import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import { logError } from "../utils/logger";
import { getUserDataOptimized, getUserDataLegacy } from '../api';

const AuthContext = createContext(null);

// Demo user data for demo mode
const mockUserData = {
  user_info: {
    id: 'demo-user-123',
    username: 'demo_creator',
    email: 'demo@solosocial.com',
    first_name: 'Demo',
    last_name: 'Creator',
    full_name: 'Demo Creator',
    profile_picture: null,
    business_name: 'TechFlow Pro',
    industry: 'Software Development',
    timezone: 'America/New_York',
    subscription_status: 'premium',
    total_posts: 247,
    connected_platforms: ['twitter', 'instagram', 'linkedin', 'facebook'],
    account_created: '2024-01-15T00:00:00Z',
    last_login: new Date().toISOString(),
    features: {
      canCreatePosts: true,
      canAccessTemplateEditor: true,
      canAccessAnalytics: true,
      canCreateCampaigns: true,
      maxPostsPerMonth: 1000,
      hasAdvancedFeatures: true
    }
  }
};

export const AuthProvider = ({ children }) => {
  // Check demo mode directly from localStorage to avoid circular dependencies
  const isDemoMode = JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
  
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('token') || isDemoMode);
  const [token, setToken] = useState(localStorage.getItem('token') || (isDemoMode ? 'demo-token' : ''));
  const [user, setUser] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [useOptimizedEndpoints, setUseOptimizedEndpoints] = useState(true);

  const fetchUserDetails = useCallback(async () => {
    const currentDemoMode = JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
    
    if (!localStorage.getItem('token') && !currentDemoMode) {
      setIsLoading(false);
      return;
    }

    try {
      let data;
      
      if (currentDemoMode) {
        // Use demo data when demo mode is enabled
        data = mockUserData;
        setUser([data.user_info]);
        setIsAuthenticated(true);
        setToken('demo-token');
      } else {
        // Try optimized endpoint first, fallback to legacy if needed
        if (useOptimizedEndpoints) {
          try {
            data = await getUserDataOptimized();
          } catch (optimizedError) {
            console.warn('Optimized endpoint failed, falling back to legacy:', optimizedError);
            setUseOptimizedEndpoints(false);
            data = await getUserDataLegacy();
          }
        } else {
          // Use legacy endpoint
          const url = `${process.env.REACT_APP_API_BASE_URL}user/details/`;
          const response = await fetch(url, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${localStorage.getItem('token')}`
            },
          });
          data = await response.json();
        }

        if (data.detail === 'Invalid token.') {
          logout();
          return;
        }

        if (data && data.user_info) {
          setUser([data.user_info]);
        } else {
          setErrorMessage(data[0] || ['Failed to load user data']);
          logError('Failed to fetch user details', {
            error: data,
            timestamp: new Date().toISOString(),
            endpoint: useOptimizedEndpoints ? 'optimized' : 'legacy'
          });
        }
      }
    } catch (error) {
      if (!currentDemoMode) {
        logError('Error fetching user details', {
          error: error.message,
          timestamp: new Date().toISOString(),
          endpoint: useOptimizedEndpoints ? 'optimized' : 'legacy'
        });
        setErrorMessage(['Failed to fetch user details']);
      }
    } finally {
      setIsLoading(false);
    }
  }, [useOptimizedEndpoints]);

  // Initial user details fetch
  useEffect(() => {
    fetchUserDetails();
  }, [token, fetchUserDetails]);

  // Listen for demo mode changes
  useEffect(() => {
    const handleStorageChange = () => {
      const currentDemoMode = JSON.parse(localStorage.getItem('solosocial_demo_mode') || 'false');
      if (currentDemoMode !== isDemoMode) {
        // Demo mode changed, refetch user details
        fetchUserDetails();
      }
    };

    // Listen for storage changes (when demo mode is toggled)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom demo mode events
    window.addEventListener('demoModeChanged', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('demoModeChanged', handleStorageChange);
    };
  }, [isDemoMode, fetchUserDetails]);

  const refreshUser = useCallback(async () => {
    setIsLoading(true);
    await fetchUserDetails();
  }, [fetchUserDetails]);

  const login = useCallback((username, password, timezone) => {
    const url = process.env.REACT_APP_API_BASE_URL + 'user/login/';

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ "email":username, username, password, timezone }),
    })
      .then(async (response) => {
        const data = await response.json();
        
        if (response.ok && data.token) {
          // Successful login
          localStorage.setItem('token', data.token);
          setToken(data.token);
          setIsAuthenticated(true);
          setUser([data.user_info]);
          setUseOptimizedEndpoints(true);
          setErrorMessage([]); // Clear any previous errors
        } else if (response.status === 429) {
          // Handle throttling (too many login attempts)
          const retryAfterMatch = data.detail?.match(/(\d+)\s+seconds?/);
          const retryAfter = retryAfterMatch ? parseInt(retryAfterMatch[1]) : 60;
          
          setErrorMessage({
            error: 'Too many login attempts. Your account is temporarily locked for security.',
            retry_after: retryAfter,
            status: 429
          });
        } else if (response.status === 403) {
          // Handle account suspension
          setErrorMessage({
            error: data.error || 'Your account is suspended. Please contact support.',
            status: 403
          });
        } else {
          // Handle other errors (400, 500, etc.)
          setErrorMessage({
            error: data.error || data.detail || 'Login failed. Please check your credentials.',
            status: response.status
          });
        }
      })
      .catch((error) => {
        // Handle network errors
        setErrorMessage({
          error: 'Network error. Please check your connection and try again.',
          status: 0
        });
        logError('Login network error', {
          error: error.message,
          timestamp: new Date().toISOString()
        });
      });
  },[]);

  const logout = useCallback(() => {

      const url = process.env.REACT_APP_API_BASE_URL + 'logout/';

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      },
    })
      .then((response) => {
        if (!response.data) {
          // Call the setToken function to store the JWT
            setIsAuthenticated(false);
            setUser([]);
            setToken('');
            localStorage.removeItem('token');
            // Reset optimized endpoints preference
            setUseOptimizedEndpoints(true);
        } else {
          setErrorMessage(response.data);
          console.error(response.data);
        }
      });

  },[token]);

  return (
    <AuthContext.Provider value={{ 
      isAuthenticated, 
      token, 
      login, 
      logout, 
      user, 
      errorMessage, 
      isLoading, 
      setErrorMessage, 
      setToken,
      refreshUser,
      useOptimizedEndpoints,
      setUseOptimizedEndpoints
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
